import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { PublicLayout } from "../components/PublicLayout";

function CodeBlock({ title, language, langKey, children }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl border border-[var(--bd)] overflow-hidden bg-[var(--surface)]">
      <div className="flex items-center justify-between px-4 py-2.5 bg-[var(--surface2)] border-b border-[var(--bd)]">
        <span className="font-heading font-bold text-sm">{title}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-[var(--t2)] uppercase">{language}</span>
          <button onClick={copy} className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-[var(--accent-soft)] transition-colors text-[var(--t2)] hover:text-[var(--accent)]">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        </div>
      </div>
      <SyntaxHighlighter
        language={langKey || "cpp"}
        style={oneDark}
        customStyle={{ margin: 0, borderRadius: 0, fontSize: "0.8rem", lineHeight: 1.6, background: "#1C2230" }}
        showLineNumbers
        lineNumberStyle={{ color: "#4a5568", fontSize: "0.7rem", minWidth: "2.5em" }}
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
}

const SNIPPETS = [
  {
    title: "WiFi + MQTT Connection",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <WiFi.h>
#include <PubSubClient.h>

const char* ssid     = "YOUR_WIFI_SSID";
const char* password = "YOUR_WIFI_PASS";
const char* mqtt_server = "broker.hivemq.com";
const int   mqtt_port   = 8883;

WiFiClient espClient;
PubSubClient mqtt(espClient);

void setupWiFi() {
  WiFi.begin(ssid, password);
  while (WiFi.status() != WL_CONNECTED) {
    delay(500);
    Serial.print(".");
  }
  Serial.println("WiFi connected: " + WiFi.localIP().toString());
}

void reconnectMQTT() {
  while (!mqtt.connected()) {
    if (mqtt.connect("ESP32_CEM", mqtt_user, mqtt_pass)) {
      mqtt.subscribe("campus/energy/cmd");
      Serial.println("MQTT connected");
    } else {
      delay(5000);
    }
  }
}

void setup() {
  Serial.begin(115200);
  setupWiFi();
  mqtt.setServer(mqtt_server, mqtt_port);
  mqtt.setCallback(onMessage);
}

void loop() {
  if (!mqtt.connected()) reconnectMQTT();
  mqtt.loop();
}`,
  },
  {
    title: "PZEM-004T 3-Phase Reading",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <PZEM004Tv30.h>

// All 3 PZEMs share Serial2 (GPIO 16 RX, GPIO 17 TX)
// Each has a unique Modbus address
PZEM004Tv30 pzem1(Serial2, 16, 17, 0x01);  // L1
PZEM004Tv30 pzem2(Serial2, 16, 17, 0x02);  // L2
PZEM004Tv30 pzem3(Serial2, 16, 17, 0x03);  // L3

void readPZEM(PZEM004Tv30 &pzem, const char* label) {
  float voltage   = pzem.voltage();
  float current   = pzem.current();
  float power     = pzem.power();
  float energy    = pzem.energy();
  float frequency = pzem.frequency();
  float pf        = pzem.pf();

  if (!isnan(voltage)) {
    Serial.printf("%s: %.1fV  %.2fA  %.1fW  PF=%.2f  %.1fHz\\n",
                  label, voltage, current, power, pf, frequency);
  } else {
    Serial.printf("%s: not connected\\n", label);
  }
}

void loop() {
  readPZEM(pzem1, "L1");
  readPZEM(pzem2, "L2");
  readPZEM(pzem3, "L3");
  delay(2000);
}`,
  },
  {
    title: "DS18B20 Temperature + Fan PWM",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <OneWire.h>
#include <DallasTemperature.h>

#define TEMP_PIN    5    // DS18B20 data pin (GPIO 5)
#define FAN_PIN     25   // TIP120 base via 1K resistor (GPIO 25)
#define FAN_CHANNEL 0    // LEDC PWM channel

OneWire oneWire(TEMP_PIN);
DallasTemperature sensors(&oneWire);

// Fan thresholds (adjustable via web dashboard)
float fanLow  = 28.0;  // 30% speed
float fanMed  = 32.0;  // 60% speed
float fanHigh = 36.0;  // 100% speed
float fanCrit = 40.0;  // 100% + buzzer alert

void setup() {
  sensors.begin();
  ledcSetup(FAN_CHANNEL, 25000, 8);  // 25kHz, 8-bit
  ledcAttachPin(FAN_PIN, FAN_CHANNEL);
}

void updateFan(float temp) {
  int speed = 0;
  if      (temp >= fanHigh) speed = 255;  // 100%
  else if (temp >= fanMed)  speed = 153;  // 60%
  else if (temp >= fanLow)  speed = 77;   // 30%
  else                      speed = 0;    // OFF

  ledcWrite(FAN_CHANNEL, speed);

  if (temp >= fanCrit) {
    triggerBuzzer(3);  // critical alert
  }
}

void loop() {
  sensors.requestTemperatures();
  float temp = sensors.getTempCByIndex(0);
  if (temp > -50) {
    updateFan(temp);
    Serial.printf("Temp: %.1f C  Fan: %d%%\\n", temp, (speed * 100) / 255);
  }
  delay(2000);
}`,
  },
  {
    title: "MQ-135 Gas Sensor (Voltage Divider)",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#define MQ135_PIN  34  // ADC input (GPIO 34)
// Voltage divider: R1=4.7K, R2=10K
// Brings 5V sensor output down to ~3.4V (safe for ESP32)

String gasQuality = "GOOD";
float  gasPpm     = 0;

void readGasSensor() {
  int raw = analogRead(MQ135_PIN);
  float voltage = (raw / 4095.0) * 3.3;

  // Convert back to actual sensor voltage (before divider)
  float sensorV = voltage * (4.7 + 10.0) / 10.0;

  // Approximate PPM (calibration curve varies by sensor)
  gasPpm = map(raw, 0, 4095, 0, 1000);

  // Classify air quality
  if      (gasPpm > 600) gasQuality = "DANGER";
  else if (gasPpm > 300) gasQuality = "POOR";
  else                   gasQuality = "GOOD";

  Serial.printf("Gas: %s (%.0f ppm, %.2fV)\\n",
                gasQuality.c_str(), gasPpm, sensorV);

  // Trigger buzzer on DANGER
  if (gasQuality == "DANGER") {
    triggerBuzzer(5);
  }
}`,
  },
  {
    title: "TIP120 Buzzer Driver",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#define BUZZER_PIN  27  // TIP120 base via R3 (1K) on GPIO 27
// TIP120 Collector -> Buzzer (-), Buzzer (+) -> 5V
// TIP120 Emitter -> GND

void triggerBuzzer(int beeps) {
  for (int i = 0; i < beeps; i++) {
    digitalWrite(BUZZER_PIN, HIGH);  // TIP120 ON -> buzzer sounds
    delay(120);
    digitalWrite(BUZZER_PIN, LOW);   // TIP120 OFF -> buzzer stops
    delay(80);
  }
}

void setup() {
  pinMode(BUZZER_PIN, OUTPUT);
  digitalWrite(BUZZER_PIN, LOW);
}`,
  },
  {
    title: "20x4 LCD Display Update",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <LiquidCrystal_I2C.h>

// I2C LCD: SDA=GPIO 21, SCL=GPIO 22, Address=0x27
LiquidCrystal_I2C lcd(0x27, 20, 4);

int lcdPage = 0;
unsigned long lastPageSwitch = 0;

void updateLCD(float temp, int fan, float voltage, float power) {
  if (millis() - lastPageSwitch > 5000) {
    lcdPage = (lcdPage + 1) % 5;  // cycle 5 pages
    lastPageSwitch = millis();
  }

  lcd.clear();
  lcd.setCursor(0, 0);
  lcd.print("== ENERGY MONITOR ==");

  switch (lcdPage) {
    case 0:  // L1 Power
      lcd.setCursor(0, 1); lcd.printf("L1: %.1fV  %.2fA", v1, i1);
      lcd.setCursor(0, 2); lcd.printf("Pwr:%4.0fW PF:%.2f", p1, pf1);
      lcd.setCursor(0, 3); lcd.printf("Frq:%.1fHz E:%.1fkWh", f1, e1);
      break;
    case 4:  // Gas Quality
      lcd.setCursor(0, 1); lcd.printf("Tmp:%.1fC Fan:%3d%%", temp, fan);
      lcd.setCursor(0, 2); lcd.printf("PIR:%-6s WiFi:%s", pir?"Motion":"Idle", "OK");
      lcd.setCursor(0, 3); lcd.printf("Gas:%-5s %4.0fppm", gasQuality, gasPpm);
      break;
  }
}`,
  },
  {
    title: "MQTT Publish Telemetry (JSON)",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `#include <ArduinoJson.h>

void publishState() {
  StaticJsonDocument<768> doc;

  doc["temperature"] = temperature;
  doc["fan_speed"]   = fanSpeed;
  doc["fan_auto"]    = fanAutoMode;
  doc["pir"]         = digitalRead(PIR_PIN);
  doc["light"]       = lightBrightness;
  doc["ir"]          = irLedState;
  doc["gas_quality"] = gasQuality;
  doc["gas_ppm"]     = gasPpm;
  doc["total_power"] = p1 + p2 + p3;
  doc["wifi"]        = WiFi.isConnected();
  doc["ip"]          = WiFi.localIP().toString();
  doc["uptime"]      = millis() / 1000;

  JsonObject l1 = doc.createNestedObject("phases").createNestedObject("L1");
  l1["voltage"]   = v1;
  l1["current"]   = i1;
  l1["power"]     = p1;
  l1["pf"]        = pf1;
  l1["frequency"] = f1;
  // ... repeat for L2, L3

  char buffer[768];
  serializeJson(doc, buffer);
  mqtt.publish("campus/energy/state", buffer);
}`,
  },
  {
    title: "MQTT Command Handler",
    language: "C++ (Arduino)",
    langKey: "cpp",
    code: `void onMessage(char* topic, byte* payload, unsigned int length) {
  String msg = "";
  for (int i = 0; i < length; i++) msg += (char)payload[i];

  if (String(topic) == "campus/energy/cmd") {
    StaticJsonDocument<256> doc;
    deserializeJson(doc, msg);

    String device = doc["device"];
    String action = doc["action"];

    if (device == "fan") {
      if      (action == "off")  setFan(0);
      else if (action == "30")   setFan(77);
      else if (action == "60")   setFan(153);
      else if (action == "100")  setFan(255);
      else if (action == "auto") fanAutoMode = true;
    }
    else if (device == "light") {
      digitalWrite(LIGHT_PIN, action == "on" ? HIGH : LOW);
    }
    else if (device == "buzzer") {
      triggerBuzzer(action.toInt());
    }
    else if (device == "system" && action == "all_off") {
      setFan(0);
      digitalWrite(LIGHT_PIN, LOW);
      digitalWrite(IR_LED_PIN, LOW);
    }
  }
}`,
  },
];

export default function CodePublic() {
  const [filter, setFilter] = useState("All");
  const tags = ["All", "Sensors", "MQTT", "Control", "Display"];

  const tagMap = {
    Sensors: [0, 1, 2, 3],
    MQTT: [0, 6, 7],
    Control: [4, 7],
    Display: [5],
  };

  const visible = filter === "All" ? SNIPPETS : SNIPPETS.filter((_, i) => tagMap[filter]?.includes(i));

  return (
    <PublicLayout>
      <section className="w-full px-4 sm:px-8 lg:px-12 py-16">
        <div className="max-w-[960px] mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <span className="uppercase tracking-[0.2em] text-[10px] font-bold text-[var(--accent)]">Source Code</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-2">Code Snippets</h1>
            <p className="text-[var(--t2)] mt-3">Core Arduino/ESP32 firmware snippets from the Campus Energy Monitor. Copy and adapt for your own IoT projects.</p>
            <p className="text-xs text-[var(--t2)] font-mono mt-2">ESP32 Arduino Core v2.0.17 · PlatformIO / Arduino IDE</p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {tags.map((t) => (
              <button key={t} onClick={() => setFilter(t)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${filter === t ? "bg-[var(--accent)] text-black border-[var(--accent)]" : "bg-[var(--surface2)] border-[var(--bd)] text-[var(--t2)] hover:border-[var(--bd-strong)]"}`}>
                {t}
              </button>
            ))}
          </div>

          <div className="space-y-6">
            {visible.map((s, i) => (
              <CodeBlock key={i} title={s.title} language={s.language} langKey={s.langKey}>{s.code}</CodeBlock>
            ))}
          </div>

          <div className="mt-12 text-center text-sm text-[var(--t2)]">
            <p>Full firmware source: <span className="font-mono text-[var(--accent)]">campus_energy_monitor.ino</span></p>
            <p className="mt-1">Built by HNK · CPUT 2026</p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
